import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ORDER_STATUS_COLORS, buildOrderStatusMap } from "../../utils/orderStatus";
import { useModal } from "../../context/ModalContext";
import Select from "../../components/Select";

const STATUS_COLORS = ORDER_STATUS_COLORS;

const API = process.env.REACT_APP_API_URL || "";

export default function AdminOrders() {
  const { showAlert, showConfirm } = useModal();
  const { t } = useTranslation();
  const STATUS_MAP = buildOrderStatusMap(t);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchType, setSearchType] = useState("customer_name");
  const [searchValue, setSearchValue] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, printing: 0, printed: 0, shipping: 0, delivered: 0, cancelled: 0, total: 0 });
  const [selectedIds, setSelectedIds] = useState(new Set());

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/orders/stats`);
      const data = await res.json();
      if (data && typeof data === "object" && !data.error) setStats(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const [returnsByOrder, setReturnsByOrder] = useState({});

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filterStatus) params.append("status", filterStatus);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (searchValue.trim()) {
        params.append("search_type", searchType);
        params.append("search_value", searchValue.trim());
      }

      const [ordersRes, returnsRes] = await Promise.all([
        fetch(`${API}/api/orders?${params}`).then(r => r.json()),
        fetch(`${API}/api/returns?limit=1000`).then(r => r.json()),
      ]);
      setOrders(ordersRes.orders || []);
      setTotal(ordersRes.total || 0);

      // 주문별 최신 신청 매핑
      const map = {};
      if (Array.isArray(returnsRes.returns)) {
        for (const r of returnsRes.returns) {
          if (!map[r.order_id]) map[r.order_id] = r;
        }
      }
      setReturnsByOrder(map);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, dateFrom, dateTo, searchType, searchValue]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // 페이지 변경 시 선택 초기화
  useEffect(() => { setSelectedIds(new Set()); }, [orders]);

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === orders.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(orders.map((o) => o.id)));
    }
  }

  async function bulkPrintInvoice() {
    if (selectedIds.size === 0) return showAlert("출력할 주문을 선택하세요.");
    // TODO: CJ대한통운 API 연동
    showAlert(`송장 출력 (준비 중)\n선택된 주문 ${selectedIds.size}건\n\nCJ대한통운 API 연동 후 사용 가능합니다.`);
  }

  async function bulkCancelPickup() {
    if (selectedIds.size === 0) return showAlert("취소할 주문을 선택하세요.");
    if (!(await showConfirm(`선택된 ${selectedIds.size}건의 집화를 취소하시겠습니까?`))) return;
    // TODO: CJ대한통운 API 연동
    showAlert(`집화 취소 (준비 중)\n선택된 주문 ${selectedIds.size}건\n\nCJ대한통운 API 연동 후 사용 가능합니다.`);
  }

  async function updateOrder(id, updates) {
    try {
      await fetch(`${API}/api/orders/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      fetchOrders();
      if (selected?.id === id) {
        setSelected((prev) => ({ ...prev, ...updates }));
      }
    } catch (e) {
      console.error(e);
      showAlert("업데이트 실패");
    }
  }

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="admin-orders">
      <h2>주문 관리</h2>

      {/* 상태별 통계 카드 */}
      <div className="ao-stats-grid">
        <button
          className={`ao-stat-card ao-stat-pending ${filterStatus === "pending" ? "active" : ""}`}
          onClick={() => { setFilterStatus("pending"); setPage(1); }}
        >
          <div className="ao-stat-top">
            <span className="ao-stat-label">결제완료</span>
            <span className="ao-stat-badge-new">NEW</span>
          </div>
          <div className="ao-stat-value">{stats.pending}</div>
        </button>

        <button
          className={`ao-stat-card ao-stat-confirmed ${filterStatus === "confirmed" ? "active" : ""}`}
          onClick={() => { setFilterStatus("confirmed"); setPage(1); }}
        >
          <div className="ao-stat-top">
            <span className="ao-stat-label">발주확인</span>
            <span className="ao-stat-icon">✔️</span>
          </div>
          <div className="ao-stat-value">{stats.confirmed}</div>
        </button>

        <button
          className={`ao-stat-card ao-stat-printing ${filterStatus === "printing" ? "active" : ""}`}
          onClick={() => { setFilterStatus("printing"); setPage(1); }}
        >
          <div className="ao-stat-top">
            <span className="ao-stat-label">제작중</span>
            <span className="ao-stat-icon">🛠</span>
          </div>
          <div className="ao-stat-value">{stats.printing}</div>
        </button>

        <button
          className={`ao-stat-card ao-stat-printed ${filterStatus === "printed" ? "active" : ""}`}
          onClick={() => { setFilterStatus("printed"); setPage(1); }}
        >
          <div className="ao-stat-top">
            <span className="ao-stat-label">배송준비</span>
            <span className="ao-stat-icon">📦</span>
          </div>
          <div className="ao-stat-value">{stats.printed}</div>
        </button>

        <button
          className={`ao-stat-card ao-stat-shipping ${filterStatus === "shipping" ? "active" : ""}`}
          onClick={() => { setFilterStatus("shipping"); setPage(1); }}
        >
          <div className="ao-stat-top">
            <span className="ao-stat-label">배송중</span>
            <span className="ao-stat-icon">🚚</span>
          </div>
          <div className="ao-stat-value">{stats.shipping}</div>
        </button>

        <button
          className={`ao-stat-card ao-stat-delivered ${filterStatus === "delivered" ? "active" : ""}`}
          onClick={() => { setFilterStatus("delivered"); setPage(1); }}
        >
          <div className="ao-stat-top">
            <span className="ao-stat-label">배송완료</span>
            <span className="ao-stat-icon">✅</span>
          </div>
          <div className="ao-stat-value">{stats.delivered}</div>
        </button>

        <button
          className={`ao-stat-card ao-stat-cancelled ${filterStatus === "cancelled" ? "active" : ""}`}
          onClick={() => { setFilterStatus("cancelled"); setPage(1); }}
        >
          <div className="ao-stat-top">
            <span className="ao-stat-label">주문취소</span>
            <span className="ao-stat-icon">↩️</span>
          </div>
          <div className="ao-stat-value">{stats.cancelled}</div>
        </button>

        <button
          className={`ao-stat-card ao-stat-total ${filterStatus === "" ? "active" : ""}`}
          onClick={() => { setFilterStatus(""); setPage(1); }}
        >
          <div className="ao-stat-top">
            <span className="ao-stat-label">총 주문</span>
            <span className="ao-stat-icon">📊</span>
          </div>
          <div className="ao-stat-value">{stats.total}</div>
        </button>
      </div>

      {/* 필터 */}
      <div className="ao-filter">
        <Select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}>
          <option value="">전체 상태</option>
          {Object.entries(STATUS_MAP).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </Select>
        <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
        <span className="ao-filter-sep">~</span>
        <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
        <Select value={searchType} onChange={(e) => setSearchType(e.target.value)} className="ao-search-type">
          <option value="customer_name">고객명</option>
          <option value="customer_phone">연락처</option>
          <option value="order_number">주문번호</option>
          <option value="tracking_number">송장번호</option>
        </Select>
        <input
          type="text"
          className="ao-search-input"
          placeholder="검색..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); fetchOrders(); } }}
        />
        <button className="ao-search-btn" onClick={() => { setPage(1); fetchOrders(); }}>검색</button>
        {(dateFrom || dateTo || searchValue || filterStatus) && (
          <button className="ao-reset-btn" onClick={() => { setFilterStatus(""); setDateFrom(""); setDateTo(""); setSearchValue(""); setPage(1); }}>초기화</button>
        )}
        <span className="ao-count">총 {total}건</span>
      </div>

      {/* 일괄 작업 툴바 */}
      <div className="ao-bulk-bar">
        <div className="ao-bulk-info">
          {selectedIds.size > 0 ? (
            <span><strong>{selectedIds.size}건</strong> 선택됨</span>
          ) : (
            <span className="ao-bulk-hint">송장 출력 / 집화 취소할 주문을 선택하세요</span>
          )}
        </div>
        <div className="ao-bulk-actions">
          <button
            className="ao-bulk-btn ao-bulk-print"
            onClick={bulkPrintInvoice}
            disabled={selectedIds.size === 0}
          >
            🖨 송장 출력
          </button>
          <button
            className="ao-bulk-btn ao-bulk-cancel"
            onClick={bulkCancelPickup}
            disabled={selectedIds.size === 0}
          >
            ↩ 집화 취소
          </button>
        </div>
      </div>

      {/* 목록 */}
      <div className="ao-table-wrap">
        <table className="ao-table">
          <thead>
            <tr>
              <th className="ao-check-col">
                <input
                  type="checkbox"
                  checked={orders.length > 0 && selectedIds.size === orders.length}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>주문번호</th>
              <th>주문일</th>
              <th>고객명</th>
              <th>연락처</th>
              <th>색상</th>
              <th>수량</th>
              <th>가격</th>
              <th>상태</th>
              <th>송장번호</th>
              <th>신청</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} style={{ textAlign: "center", padding: 40 }}>로딩 중...</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={12} style={{ textAlign: "center", padding: 40 }}>주문이 없습니다.</td></tr>
            ) : orders.map((o) => (
              <tr key={o.id} className={selectedIds.has(o.id) ? "ao-row-selected" : ""}>
                <td className="ao-check-col">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(o.id)}
                    onChange={() => toggleSelect(o.id)}
                  />
                </td>
                <td style={{ fontSize: 12, color: "#888" }}>{o.order_number || "-"}</td>
                <td>{new Date(o.created_at).toLocaleDateString("ko-KR")}</td>
                <td>{o.customer_name}</td>
                <td>{o.customer_phone}</td>
                <td>{(Array.isArray(o.items) ? o.items : []).map((it) => it.color).filter(Boolean).join(", ") || "-"}</td>
                <td>{(Array.isArray(o.items) ? o.items : []).reduce((s, it) => s + (it.quantity || 0), 0) || "-"}</td>
                <td>{(o.total_price || o.shipping_cost) ? `${((o.total_price || 0) + (o.shipping_cost || 0)).toLocaleString()}원` : "-"}</td>
                <td>
                  <Select
                    className="ao-status-select"
                    value={o.status}
                    style={{ background: STATUS_COLORS[o.status], color: "#fff", borderColor: STATUS_COLORS[o.status] }}
                    onChange={(e) => updateOrder(o.id, { status: e.target.value })}
                  >
                    {Object.entries(STATUS_MAP).map(([k, v]) => (
                      <option key={k} value={k} style={{ background: "#fff", color: STATUS_COLORS[k] }}>{v}</option>
                    ))}
                  </Select>
                </td>
                <td>
                  <div className="ao-tracking-inline">
                    <input
                      defaultValue={o.tracking_number || ""}
                      placeholder="송장번호"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") updateOrder(o.id, { tracking_number: e.target.value, status: "shipping" });
                      }}
                    />
                    <button onClick={(e) => {
                      const input = e.target.previousSibling;
                      updateOrder(o.id, { tracking_number: input.value, status: input.value ? "shipping" : o.status });
                    }}>저장</button>
                  </div>
                </td>
                <td>
                  {(() => {
                    const req = returnsByOrder[o.id];
                    if (!req) return "-";
                    const rtype = req.request_type || req.type;
                    const label = rtype === "cancel" ? "취소" : rtype === "exchange" ? "교환" : "환불";
                    const statusLabel = req.status === "pending" ? "신청"
                      : req.status === "approved" ? "승인"
                      : req.status === "collecting" ? "수거중"
                      : req.status === "completed" ? "완료"
                      : req.status === "rejected" ? "거절"
                      : req.status === "withdrawn" ? "철회"
                      : req.status;
                    const color = req.status === "pending" ? "#f59e0b"
                      : req.status === "completed" ? "#10b981"
                      : req.status === "rejected" ? "#ef4444"
                      : req.status === "withdrawn" ? "#A8968A"
                      : "#C8813F";
                    return (
                      <span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: color, color: "#fff" }}>
                        {label} {statusLabel}
                      </span>
                    );
                  })()}
                </td>
                <td>
                  <button className="ao-detail-btn" onClick={() => setSelected(o)}>상세</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="ao-pagination">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}>이전</button>
          <span>{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>다음</button>
        </div>
      )}

      {/* 상세 모달 */}
      {selected && (
        <div className="ao-modal-overlay" onClick={() => setSelected(null)}>
          <div className="ao-modal" onClick={(e) => e.stopPropagation()}>
            <h3>주문 상세</h3>
            <button className="ao-modal-close" onClick={() => setSelected(null)}>✕</button>

            <div className="ao-modal-body">
            <div className="ao-detail-sections">
              <div className="ao-detail-group">
                <h4>주문 정보</h4>
                <div className="ao-detail-grid">
                  <div><strong>주문번호</strong><span>{selected.order_number || "-"}</span></div>
                  <div><strong>주문일</strong><span>{new Date(selected.created_at).toLocaleString("ko-KR")}</span></div>
                  <div><strong>고객명</strong><span>{selected.customer_name}</span></div>
                  <div><strong>연락처</strong><span>{selected.customer_phone}</span></div>
                </div>
              </div>

              <div className="ao-detail-group">
                <h4>주문 항목 ({(selected.items || []).length})</h4>
                {Array.isArray(selected.items) && selected.items.length > 0 ? (
                  <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#F4EADB" }}>
                        <th style={{ padding: 6, textAlign: "left" }}>#</th>
                        <th style={{ padding: 6, textAlign: "left" }}>파일</th>
                        <th style={{ padding: 6 }}>재료</th>
                        <th style={{ padding: 6 }}>색상</th>
                        <th style={{ padding: 6 }}>수량</th>
                        <th style={{ padding: 6, textAlign: "right" }}>금액</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.items.map((it, i) => (
                        <tr key={i} style={{ borderTop: "1px solid #E7DAC8" }}>
                          <td style={{ padding: 6 }}>{i + 1}</td>
                          <td style={{ padding: 6 }}>
                            {it.thumbnailUrl && <img src={it.thumbnailUrl} alt="" style={{ width: 40, height: 40, objectFit: "cover", verticalAlign: "middle", marginRight: 6, borderRadius: 4 }} />}
                            {it.fileName}
                          </td>
                          <td style={{ padding: 6, textAlign: "center" }}>{it.material}</td>
                          <td style={{ padding: 6, textAlign: "center" }}>{it.color}</td>
                          <td style={{ padding: 6, textAlign: "center" }}>{it.quantity}</td>
                          <td style={{ padding: 6, textAlign: "right" }}>{(it.price || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ color: "#888" }}>항목 없음</p>
                )}
                {selected.memo && <div style={{ marginTop: 12 }}><strong>요청사항:</strong> {selected.memo}</div>}
              </div>

              <div className="ao-detail-group">
                <h4>배송 정보</h4>
                <div className="ao-detail-grid">
                  <div><strong>수령인</strong><span>{selected.recipient_name || selected.customer_name}</span></div>
                  <div><strong>수령인 연락처</strong><span>{selected.recipient_phone || selected.customer_phone}</span></div>
                  <div><strong>배송유형</strong><span>{selected.shipping_type === "domestic" ? "국내배송" : "해외배송"}</span></div>
                  <div><strong>주소</strong><span>{selected.zipcode && `(${selected.zipcode}) `}{selected.address} {selected.address_detail}</span></div>
                  {selected.shipping_type === "international" && (
                    <>
                      <div><strong>Country</strong><span>{selected.country}</span></div>
                      <div><strong>City / State</strong><span>{selected.city} {selected.state}</span></div>
                    </>
                  )}
                  {selected.delivery_memo && <div><strong>배송메모</strong><span>{selected.delivery_memo}</span></div>}
                </div>
              </div>

              <div className="ao-detail-group">
                <h4>결제 정보</h4>
                <div className="ao-detail-grid">
                  <div><strong>출력 비용</strong><span>{selected.total_price ? `${selected.total_price.toLocaleString()}원` : "-"}</span></div>
                  <div><strong>배송비</strong><span>{selected.shipping_cost ? `${selected.shipping_cost.toLocaleString()}원` : "-"}</span></div>
                  <div className="ao-detail-total"><strong>총 결제 금액</strong><span>{((selected.total_price || 0) + (selected.shipping_cost || 0)).toLocaleString()}원</span></div>
                </div>
              </div>
            </div>

            {/* 상태 변경 */}
            <div className="ao-status-section">
              <label>
                상태 변경
                <Select
                  value={selected.status}
                  onChange={(e) => updateOrder(selected.id, { status: e.target.value })}
                >
                  {Object.entries(STATUS_MAP).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </Select>
              </label>
            </div>

            {/* 송장번호 입력 */}
            <div className="ao-tracking-section">
              <label>
                송장번호
                <div className="ao-tracking-input">
                  <input
                    type="text"
                    defaultValue={selected.tracking_number || ""}
                    id="tracking-input"
                    placeholder="CJ 송장번호 입력"
                  />
                  <button onClick={() => {
                    const val = document.getElementById("tracking-input").value.trim();
                    if (!val) return showAlert("송장번호를 입력하세요.");
                    updateOrder(selected.id, { tracking_number: val, status: "shipping" });
                  }}>
                    저장
                  </button>
                </div>
              </label>
            {/* PayPal 환불 */}
            {selected.paypal_capture_id && selected.status !== "cancelled" && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #E7DAC8" }}>
                <button
                  type="button"
                  onClick={async () => {
                    if (!(await showConfirm("PayPal에서 결제 전액을 환불합니다.\n환불 후에는 취소할 수 없습니다. 진행하시겠습니까?"))) return;
                    try {
                      const res = await fetch(`${API}/api/paypal/refund`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ capture_id: selected.paypal_capture_id, note: `Order ${selected.order_number} refund from admin` }),
                      });
                      const data = await res.json();
                      if (res.ok) {
                        showAlert(`환불 완료\n환불 ID: ${data.refund_id}`);
                        updateOrder(selected.id, { status: "cancelled" });
                      } else {
                        showAlert(`환불 실패: ${data.error || JSON.stringify(data)}`);
                      }
                    } catch (e) {
                      showAlert("환불 요청 실패: " + e.message);
                    }
                  }}
                  style={{ width: "100%", padding: "12px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                >
                  💳 PayPal 환불 처리
                </button>
              </div>
            )}

            {/* 신청 상태 */}
            {returnsByOrder[selected.id] && (
              <div style={{ marginTop: 12, padding: "10px 14px", background: "#FBF6EE", border: "1px solid #E7DAC8", borderRadius: 8, fontSize: 13 }}>
                <strong>신청:</strong> {(() => {
                  const req = returnsByOrder[selected.id];
                  const rtype = req.request_type || req.type;
                  const label = rtype === "cancel" ? "취소" : rtype === "exchange" ? "교환" : "환불";
                  return `${label} — ${req.status} ${req.reject_reason ? `(거절 사유: ${req.reject_reason})` : ""}`;
                })()}
              </div>
            )}
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
